import * as React from "react";
import {GeneralContext} from "../../core/general.context";
import {LabelContext} from "../../core/label.context";
import {Button} from "../button/button.component";
import {InputElement} from "../input/input.component";
import {useForm, SubmitHandler} from "react-hook-form";
import {yupResolver} from '@hookform/resolvers/yup';
import * as yup from "yup";
import {inputMaxLength, toConnectifDate} from "../../common/constants";
import {BRX_BASE_URL, ES_INDEX, calls, PROJECT_COUNTRY, PROJECT_KEY} from "../../core/APIUtils";
import {RESTContext} from "../../core/rest.context";


export const EditUserData = (props: any) => {
    const {callback} = props;
    const {userData, setUserData} = React.useContext(GeneralContext)
    const {ajax$, ajaxBRX$, ajaxMiddleware$} = React.useContext(RESTContext)
    const {t} = React.useContext(LabelContext)
    let requiredError = t("ecommerce.form-messagge.required")
    let emailError = t("ecommerce.form-messagge.invalid-email")
    let inputMaxLengthError = t("ecommerce.form-messagge.error-input-max-length").replace("[NUMBER]", inputMaxLength.toString())
    let [profiling, setProfiling] = React.useState(userData?.custom?.fields?.consentProfiling || false)


    const schema = yup.object().shape({
        email: yup
            .string()
            .email(emailError)
            .required(requiredError),
        name: yup.string().max(inputMaxLength, inputMaxLengthError).required(requiredError),
        surname: yup.string().max(inputMaxLength, inputMaxLengthError).required(requiredError),
        birthday: yup
            .date()
            .nullable()
            .transform((curr, orig) => orig === '' ? null : curr),
        sex: yup.string().max(inputMaxLength, inputMaxLengthError).nullable(),
        profiling: yup.bool().when({
            is: () => ["DE"].includes(PROJECT_COUNTRY),
            then: yup.bool().oneOf([true, false])
        }),
    });

    const {register, handleSubmit, formState: {errors}, setValue, setError} = useForm({
        resolver: yupResolver(schema),
        defaultValues: {
            sex: userData.salutation,
            birthday: userData.dateOfBirth,
            name: userData.firstName,
            surname: userData.lastName,
            profiling: userData?.custom?.fields?.consentProfiling,
            email: userData.email
        },
        mode: "all",
        criteriaMode: "all",
        reValidateMode: "onChange"
    });

    const onSubmit: SubmitHandler<any> = data => {
        const actions: any[] = []
        let performChangeEmail = false

        // Nome
        if (data.name && data.name !== userData.firstName) {
            actions.push({action: "setFirstName", firstName: data.name})
        }

        // Cognome
        if (data.surname && data.surname !== userData.lastName) {
            actions.push({action: "setLastName", lastName: data.surname})
        }

        // Saluto / Sesso
        if (data.sex !== undefined && data.sex !== userData.salutation) {
            actions.push({action: "setSalutation", salutation: data.sex})
        }

        // Email
        if (data.email && data.email !== userData.email) {
            performChangeEmail = true
            actions.push({action: "changeEmail", email: data.email})
            actions.push({
                action: "setCustomField",
                name: "subscribed",
                value: false,
            })
        }

        // Data di nascita
        const formattedDate = data.birthday
            ? `${data.birthday.getFullYear()}-${data.birthday.getMonth() + 1}-${data.birthday.getDate()}`
            : null

        if ((formattedDate && formattedDate !== userData.dateOfBirth) || (!formattedDate && userData.dateOfBirth)) {
            actions.push({
                action: "setDateOfBirth",
                dateOfBirth: formattedDate,
            })
        }

        // Consenso profilazione (solo se cambia)
        if (data.profiling !== undefined && data.profiling !== userData?.custom?.fields?.consentProfiling) {
            actions.push({
                action: "setCustomField",
                name: "consentProfiling",
                value: data.profiling,
            })
        }

        // Se non c'è nulla da aggiornare, non fare la chiamata
        if (!actions.length) {
            callback(false)
            return
        }

        const body = {
            version: userData.version,
            actions,
        }

        ajax$(calls.Project.updateCustomer(), undefined, body).subscribe({
            next: (res) => {
                setUserData(res)
                if (data.birthday && ES_INDEX) {
                    ajaxMiddleware$(calls.Project.updateConnectifCustomerInfo(), undefined, {
                        params: JSON.stringify({_birthdate: toConnectifDate(data.birthday)}),
                        es_index: ES_INDEX,
                        email: res.email
                    }).subscribe({error: () => {}})
                }
                if(performChangeEmail){
                    let body_verify = {
                        "ttlMinutes": 720,
                        "id": res.id,
                        "projectKey": PROJECT_KEY,
                        "baseUrl": BRX_BASE_URL
                    }
                    ajaxBRX$(calls.Project.sendVerifyMail(), undefined, body_verify).subscribe()
                }
                callback(false)
            },
            error: (err) => {
                const duplicateEmailError =
                    err?.response?.statusCode === 400 &&
                    Array.isArray(err?.response?.errors) &&
                    err?.response?.errors.some((e: any) => e.code === "DuplicateField" && e.field === "email")

                if (duplicateEmailError) {
                    setError("email", {
                        type: "manual",
                        message: t("ecommerce.form-messagge.email-already-exists"),
                    })
                    return
                }
            }
        })
    }

    return (
        <form onSubmit={handleSubmit(onSubmit)}>
            <div className="row">
                <InputElement
                    externalRegister={register("email")}
                    className="col-md-9 col-12"
                    label={`* ${t("ecommerce.personalinfo.email")}`}
                    placeholder={t("ecommerce.personalinfo.email")}
                    id="email"
                    type="email"
                    errors={errors.email?.message}
                />
                <InputElement
                    externalRegister={register("name")}
                    className="col-md-9 col-12"
                    label={t("ecommerce.signup.firsName")}
                    placeholder={t("ecommerce.signup.firsName")}
                    id="name" errors={errors.name?.message}/>
                <InputElement
                    externalRegister={register("surname")}
                    name="surname"
                    className="col-md-9 col-12"
                    label={t("ecommerce.signup.lastName")}
                    placeholder={t("ecommerce.signup.lastName")}
                    id="surname"
                    errors={errors.surname?.message}/>
                <InputElement
                    externalRegister={register("birthday")}
                    name="birthday"
                    className="col-md-9 col-12"
                    type="date" label={t("ecommerce.signup.birthday")}
                    id="birthday"
                    errors={errors.birthday?.message}/>

                <div className="col-12">

                    <label className="label-for-radio mt-0 font-weight-bold">{t("ecommerce.signup.gender")}</label>
                    <div className="radioGroup__generic">
                        <div className="radioButton">
                            <input {...register("sex")} type="radio" id="male" name="sex" value="male"/>
                            <label htmlFor="male">{t("ecommerce.signup.male")}</label>
                        </div>
                        <div className="radioButton">
                            <input {...register("sex")} type="radio" id="female" name="sex" value="female"/>
                            <label htmlFor="female">{t("ecommerce.signup.female")}</label>
                        </div>
                        <div className="radioButton">
                            <input {...register("sex")} type="radio" id="other" name="sex" value="other"/>
                            <label htmlFor="other">{t("ecommerce.signup.other")}</label>
                        </div>
                        {errors.sex?.message && <p className="message--alert small">{errors.sex?.message}</p>}
                    </div>

                    {["DE"].includes(PROJECT_COUNTRY) && (
                        <>
                            <div className="checkboxButton">
                                <>
                                    <input {...register("profiling", {required: true})} checked={profiling}
                                           type="checkbox" id="profiling" name="profiling" onChange={() => {
                                        setProfiling(!profiling);
                                        setValue("profiling", !profiling)
                                    }}/>
                                    <label htmlFor="profiling">{t("ecommerce.signup.profiling-flag")}</label>
                                </>
                            </div>
                        </>
                    )}


                    <div className="row">
                        <div className="col-md-3 offset-md-1">
                            <Button className="primary actionForm" label={t("ecommerce.common.save")} type="submit"/>
                        </div>
                        <div className="col-md-3 offset-md-1">
                            <Button className="secondary actionForm " label={t("ecommerce.common.cancel")} type="button"
                                    onClick={() => callback(false)}/>
                        </div>
                    </div>

                </div>
            </div>
        </form>

    )
};